import { useMemo, useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import { filter, take, toLower, some } from 'lodash'
import Popper from '@mui/material/Popper'
import Paper from '@mui/material/Paper'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import FolderIcon from '@mui/icons-material/Folder'
import SettingsIcon from '@mui/icons-material/Settings'
import DnsIcon from '@mui/icons-material/Dns'
import LanguageIcon from '@mui/icons-material/Language'
import { useProjectsSafe } from '../../../contexts/ProjectsContext'
import { useServicesSafe } from '../../../contexts/ServicesContext'
import { useNodesSafe } from '../../../contexts/NodesContext'
import { useDomainsSafe } from '../../../contexts/DomainsContext'

const MAX_RESULTS_PER_CATEGORY = 5
const MIN_SEARCH_LENGTH = 2

function matchesQuery(query, ...fields) {
  const lowerQuery = toLower(query)
  return some(fields, (field) => field && toLower(field).includes(lowerQuery))
}

function SearchResults({ searchValue, anchorEl, open, onClose }) {
  const navigate = useNavigate()
  const { teamSlug } = useParams()
  const { projects = [], projectsById = {} } = useProjectsSafe()
  const { services = [] } = useServicesSafe()
  const { nodes = [] } = useNodesSafe()
  const { domains = [] } = useDomainsSafe()
  const [activeIndex, setActiveIndex] = useState(-1)

  const query = (searchValue || '').trim()
  const isOpen = open && query.length >= MIN_SEARCH_LENGTH && Boolean(anchorEl)

  const results = useMemo(() => {
    if (!isOpen || !teamSlug) return []

    const sections = []

    const matchedProjects = take(
      filter(projects, (p) => matchesQuery(query, p.name, p.slug)),
      MAX_RESULTS_PER_CATEGORY
    )
    if (matchedProjects.length > 0) {
      sections.push({
        labelId: 'navigation.search.categories.projects',
        icon: <FolderIcon />,
        items: matchedProjects.map((p) => ({
          id: `project-${p.id}`,
          primary: p.name,
          secondary: p.slug,
          path: `/teams/${teamSlug}/docker/projects/${p.slug}`,
        })),
      })
    }

    const matchedServices = take(
      filter(services, (s) => matchesQuery(query, s.name, s.slug, s.image)),
      MAX_RESULTS_PER_CATEGORY
    )
    if (matchedServices.length > 0) {
      sections.push({
        labelId: 'navigation.search.categories.services',
        icon: <SettingsIcon />,
        items: matchedServices.map((s) => {
          const project = projectsById[s.dockerProjectId]
          const projectSlug = project ? project.slug : '_'
          return {
            id: `service-${s.id}`,
            primary: s.name,
            secondary: s.image,
            path: `/teams/${teamSlug}/docker/projects/${projectSlug}/services/${s.slug}`,
          }
        }),
      })
    }

    const matchedNodes = take(
      filter(nodes, (n) => matchesQuery(query, n.name, n.publicIpAddress)),
      MAX_RESULTS_PER_CATEGORY
    )
    if (matchedNodes.length > 0) {
      sections.push({
        labelId: 'navigation.search.categories.nodes',
        icon: <DnsIcon />,
        items: matchedNodes.map((n) => ({
          id: `node-${n.id}`,
          primary: n.name,
          secondary: n.publicIpAddress,
          path: `/teams/${teamSlug}/nodes/${n.id}`,
        })),
      })
    }

    const matchedDomains = take(
      filter(domains, (d) => matchesQuery(query, d.hostname)),
      MAX_RESULTS_PER_CATEGORY
    )
    if (matchedDomains.length > 0) {
      sections.push({
        labelId: 'navigation.search.categories.domains',
        icon: <LanguageIcon />,
        items: matchedDomains.map((d) => ({
          id: `domain-${d.id}`,
          primary: d.hostname,
          path: `/teams/${teamSlug}/networking/domains`,
        })),
      })
    }

    return sections
  }, [query, isOpen, teamSlug, projects, services, nodes, domains, projectsById])

  const flatItems = useMemo(
    () => results.flatMap((section) => section.items),
    [results]
  )

  const totalCount = flatItems.length

  useEffect(() => {
    setActiveIndex(-1)
  }, [query])

  const handleSelect = useCallback(
    (path) => {
      navigate(path)
      onClose()
    },
    [navigate, onClose]
  )

  const handleKeyDown = useCallback(
    (event) => {
      if (!isOpen || totalCount === 0) return

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          setActiveIndex((prev) => (prev < totalCount - 1 ? prev + 1 : 0))
          break
        case 'ArrowUp':
          event.preventDefault()
          setActiveIndex((prev) => (prev > 0 ? prev - 1 : totalCount - 1))
          break
        case 'Enter':
          event.preventDefault()
          if (activeIndex >= 0 && activeIndex < totalCount) {
            handleSelect(flatItems[activeIndex].path)
          }
          break
        default:
          break
      }
    },
    [isOpen, totalCount, activeIndex, flatItems, handleSelect]
  )

  useEffect(() => {
    if (!isOpen) return
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleKeyDown])

  if (!isOpen) return null

  let globalIndex = -1

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="bottom-start"
      sx={{ zIndex: (theme) => theme.zIndex.modal, width: anchorEl ? anchorEl.offsetWidth : 500 }}
    >
      <Paper
        elevation={8}
        sx={{
          maxHeight: 400,
          overflow: 'auto',
          mt: 0.5,
          borderRadius: 2,
        }}
      >
        {totalCount === 0 ? (
          <Typography
            variant="body2"
            sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}
          >
            <FormattedMessage id="navigation.search.noResults" />
          </Typography>
        ) : (
          results.map((section) => (
            <List key={section.labelId} disablePadding>
              <ListItem sx={{ pt: 1.5, pb: 0.5, px: 2 }}>
                <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                  {section.icon}
                </ListItemIcon>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'text.secondary',
                  }}
                >
                  <FormattedMessage id={section.labelId} />
                </Typography>
              </ListItem>
              {section.items.map((item) => {
                globalIndex += 1
                const itemIndex = globalIndex
                return (
                  <ListItem key={item.id} disablePadding>
                    <ListItemButton
                      selected={itemIndex === activeIndex}
                      onClick={() => handleSelect(item.path)}
                      sx={{ py: 0.5, px: 2, pl: 6 }}
                    >
                      <ListItemText
                        primary={item.primary}
                        secondary={item.secondary}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                      />
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
          ))
        )}
      </Paper>
    </Popper>
  )
}

export default SearchResults
